<template>
  <content-modal
    ref="studentTooltipModal"
    :visible="visible"
    :close-aria-label="closeAriaLabel"
    :size="size"
    :auto-hide-off="autoHideOff"
    class="student-tooltip-modal"
    @modal-hide-request="handleHideRequest"
  >
    <template #title>{{ t("student_tooltip_modal_title") }}</template>
    <template #content>
      <modal-content>
        <template #content>
          <student-tooltip-settings
            :task="task"
            :selected-tooltip="params.selectedTooltip"
            @is-dirty="handleIsDirty"
            @validated="handleValidated"
            @save-data="handleSaveData"
          />
        </template>
        <template #tools>
          <cv-button class="cancel" kind="secondary" size="field" @click="handleExitAction">
            {{ t("button_cancel") }}
          </cv-button>
          <fetch-data-button
            kind="primary"
            size="field"
            :loading="processingState.isLoading"
            :error="processingState.error"
            :error-message="processingState.errorMessage"
            :tip-position="dataSaveBtnTipPosition"
            @click="handleSave"
          >
            <span v-if="processingState.error">{{ t("button_state_error") }}</span>
            <span v-else>{{ primaryButtonLabel }}</span>
          </fetch-data-button>
        </template>
      </modal-content>
    </template>
  </content-modal>
</template>

<script setup lang="ts">
import { notDefined } from "@utils/var.check";
import DOMUtils from "@utils/dom.utils";
import StudentTooltipSettings from "./StudentTooltipSettings.vue";
import studentToolTipEventBus from "@plugins/richtext/plugins/student-tooltip/service/event-bus";
import EventBusUtils from "@plugins/common/service/event.bus.utils";
import { ref, computed, useTemplateRef, onUnmounted } from "vue";
import { useI18n } from "vue-i18n";
import { useModal } from "@plugins/modal/composables/modal-instance.ts";

// Props
const props = defineProps({
  params: { type: Object, required: true },
});

const { t } = useI18n();
const modal = useModal();
const studentTooltipModal = useTemplateRef("studentTooltipModal");

// State
const closeAriaLabel = ref("Close");
const size = ref("medium");
const autoHideOff = ref(true);
const visible = ref(true);
const currentAction = ref("");
const task = ref("");
const dataSaveBtnTipPosition = "top";

const processingState = ref({
  isLoading: false,
  error: false,
  errorMessage: "",
});

// Computed
const primaryButtonLabel = computed(() =>
  notDefined(props.params.selectedTooltip) ? t("button_add") : t("button_save"),
);

// Methods
const isProcessing = () => processingState.value.isLoading;
const setProcessing = () => {
  processingState.value.isLoading = true;
};
const resetProcessingState = () => {
  processingState.value.isLoading = false;
  processingState.value.error = false;
};
const setProcessingError = () => {
  processingState.value.isLoading = false;
  processingState.value.error = true;
  setTimeout(resetProcessingState, 3000);
};

const doCheckDirty = () => {
  task.value = "checkDirty";
};
const doValidate = () => {
  task.value = "validate";
};
const doSave = () => {
  task.value = "save";
};
const resetTask = () => {
  task.value = "";
};

const handleIsDirty = (isDirty: boolean) => {
  if (!isDirty) {
    hide();
    return;
  }
  if (currentAction.value === "save") {
    doValidate();
  } else if (currentAction.value === "exit") {
    resetTask();
    promptPageDirty();
  }
};

const handleValidated = (result) => {
  if (result.code === "ok") {
    doSave();
  } else {
    resetTask();
    setProcessingError();
  }
};

const handleSaveData = (result) => {
  if (result.code === "ok") {
    studentToolTipEventBus.$emit("addStudentTooltip", result.data);
  } else {
    resetTask();
    setProcessingError();
  }
};

const handleSave = () => {
  if (isProcessing()) return;
  setProcessing();
  currentAction.value = "save";
  doValidate();
};

const handleExitAction = () => {
  currentAction.value = "exit";
  setProcessing();
  doCheckDirty();
};

const handleHideRequest = (event) => {
  if (event["cv:reason"] !== "external-click") {
    handleExitAction();
  }
};

const promptPageDirty = () => {
  if (confirm(t("dirty_confirmation_prompt"))) {
    hide();
  } else {
    currentAction.value = "";
    resetProcessingState();
  }
};

const hide = () => {
  EventBusUtils.unWatchInstance(props.params.eventBusWatchInstances);
  DOMUtils.removeClass("body", "bx--body--with-modal-open");
  modal.remove(props.params.modalId);
};

onUnmounted(() => {
  hide();
});
</script>
